import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { css } from '@flipster/styled-system/css';
import { flex, grid, hstack } from '@flipster/styled-system/patterns';

import { marketSymbolBunja } from '../state';
import { Sort, TabValueType } from '../types';
import { TrendBar } from './trend-bar';
import { TrendInfo } from './trend-info';

interface Props {
  onChangeTab: (tab: TabValueType, sort?: Sort) => void;
}

export const MarketTrend = ({ onChangeTab }: Props) => {
  const { isLoadingAtom, symbolsByMarketTabAtom } = useBunja(marketSymbolBunja);
  const isLoading = useAtomValue(isLoadingAtom);
  const symbols = useAtomValue(symbolsByMarketTabAtom);

  const trendUp = symbols?.data.marketTrend1h.up ?? 0;
  const neutral = symbols?.data.marketTrend1h.neutral ?? 0;
  const trendDown = symbols?.data.marketTrend1h.down ?? 0;
  const total = trendUp + neutral + trendDown;

  return (
    <section
      className={css({
        mt: { base: 8, tablet: 24 },
        mb: { base: 32, tablet: 80 },
        px: { base: 16, tablet: 0 },
      })}
    >
      <div
        className={flex({
          bgColor: 'background.default.level-1',
          rounded: 16,
          flexDirection: { base: 'column', tablet: 'row' },
          py: { base: 20, tablet: 16 },
          px: { base: 20, tablet: 28 },
          gap: { base: 16, tablet: 56 },
        })}
      >
        <h3
          className={hstack({
            textStyle: 'Title/XS',
            color: 'content.default.level-5',
            gap: { base: 2, tablet: 4 },
          })}
        >
          <FormattedMessage id='Market.r250919.Market_trend' />
          <span className={css({ textStyle: 'Text/M/Regular' })}>
            <FormattedMessage id='Common.r250919.Hour_h_bracket' values={{ hour: 1 }} />
          </span>
        </h3>
        <div
          className={grid({
            flex: 1,
            alignItems: 'center',
            gridTemplateAreas: {
              base: `
              "bar     bar"
              "trendUp trendDown"
            `,
              tablet: '"trendUp bar trendDown"',
            },
            gridTemplateColumns: { tablet: 'auto 1fr auto' },
            gap: { base: 8, tablet: 24 },
          })}
        >
          <div className={css({ gridArea: 'trendUp', justifySelf: 'flex-start' })}>
            <TrendInfo
              isLoading={isLoading}
              trend='up'
              isMoreThanOrEqualOpposite={trendUp >= trendDown}
              coinCount={trendUp}
              oppositeCoinCount={trendDown}
              onChangeTab={onChangeTab}
            />
          </div>
          <div className={css({ gridArea: 'bar' })}>
            <TrendBar trendUp={trendUp} neutral={neutral} trendDown={trendDown} total={total} />
          </div>
          <div
            className={css({
              gridArea: 'trendDown',
              justifySelf: 'flex-end',
            })}
          >
            <TrendInfo
              isLoading={isLoading}
              trend='down'
              isMoreThanOrEqualOpposite={trendDown >= trendUp}
              coinCount={trendDown}
              oppositeCoinCount={trendUp}
              onChangeTab={onChangeTab}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
